// @ts-nocheck
import { useState } from 'react';
import {
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@/components/ui';
import {
  DynamicForm,
  MultiStepForm,
  SearchableSelect,
} from '@/components/advanced/forms';

export default function DynamicFormsPage() {
  // ---------------------------------------------------------------------------
  // DynamicForm mock data
  // ---------------------------------------------------------------------------
  const contactFields = [
    {
      name: 'fullName',
      label: 'Full Name',
      type: 'text',
      placeholder: 'Jane Doe',
      required: true,
      span: 1,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      placeholder: 'jane@example.com',
      required: true,
      rules: [{ type: 'email' }],
      span: 1,
    },
    {
      name: 'department',
      label: 'Department',
      type: 'select',
      placeholder: 'Select a department',
      options: [
        { value: 'eng', label: 'Engineering' },
        { value: 'sales', label: 'Sales' },
        { value: 'ops', label: 'Operations' },
        { value: 'hr', label: 'Human Resources' },
      ],
      span: 1,
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'slider',
      min: 0,
      max: 10,
      step: 1,
      defaultValue: 5,
      span: 1,
    },
    {
      name: 'message',
      label: 'Message',
      type: 'textarea',
      placeholder: 'Tell us how we can help...',
      rows: 4,
      span: 2,
    },
    {
      name: 'subscribe',
      label: 'Subscribe to product updates',
      type: 'switch',
      defaultValue: false,
      span: 2,
    },
  ];

  // ---------------------------------------------------------------------------
  // MultiStepForm mock data
  // ---------------------------------------------------------------------------
  const onboardingSteps = [
    {
      title: 'Account',
      description: 'Login details',
      content: (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Step 1 — Account
          </Text>
          <Text size="sm" c="dimmed">
            Collect the user's email and a password to create their account.
          </Text>
        </Stack>
      ),
    },
    {
      title: 'Profile',
      description: 'Personal info',
      content: (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Step 2 — Profile
          </Text>
          <Text size="sm" c="dimmed">
            Capture name, role and team so colleagues can find the new member.
          </Text>
        </Stack>
      ),
    },
    {
      title: 'Review',
      description: 'Confirm',
      content: (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Step 3 — Review
          </Text>
          <Text size="sm" c="dimmed">
            Show a summary of everything entered before final submission.
          </Text>
        </Stack>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // SearchableSelect mock data (controlled)
  // ---------------------------------------------------------------------------
  const assigneeOptions = [
    { value: 'u1', label: 'Oscar Martinez', description: 'Finance', group: 'Finance' },
    { value: 'u2', label: 'Angela Smith', description: 'Accounting', group: 'Finance' },
    { value: 'u3', label: 'Jim Halpert', description: 'Sales Lead', group: 'Sales' },
    { value: 'u4', label: 'Pam Beesly', description: 'Office Admin', group: 'Operations' },
    { value: 'u5', label: 'Kevin Malone', description: 'Accounting', group: 'Finance' },
  ];
  const [assignee, setAssignee] = useState<string | null>('u1');

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Dynamic Forms</Title>
        <Text c="dimmed" size="sm">
          Schema-driven and multi-step form building blocks for capturing
          structured input, plus an async-friendly searchable select.
        </Text>
      </Stack>

      {/* DynamicForm */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DynamicForm</Title>
            <Text size="sm" c="dimmed">
              Renders a complete form from a field schema, with built-in
              validation, layout columns and submit handling.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <DynamicForm
              fields={contactFields}
              columns={2}
              showReset
              submitLabel="Send Request"
              onSubmit={(values) => console.log('Submitted:', values)}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const fields = [
  {
    name: 'fullName',
    label: 'Full Name',
    type: 'text',
    placeholder: 'Jane Doe',
    required: true,
    span: 1,
  },
  {
    name: 'email',
    label: 'Email Address',
    type: 'email',
    required: true,
    rules: [{ type: 'email' }],
    span: 1,
  },
  {
    name: 'department',
    label: 'Department',
    type: 'select',
    options: [
      { value: 'eng', label: 'Engineering' },
      { value: 'sales', label: 'Sales' },
    ],
    span: 1,
  },
  {
    name: 'message',
    label: 'Message',
    type: 'textarea',
    rows: 4,
    span: 2,
  },
];

<DynamicForm
  fields={fields}
  columns={2}
  showReset
  submitLabel="Send Request"
  onSubmit={(values) => {
    // Handle submitted values
  }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key Props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • fields - Array of field configs (name, type, label, rules...)
              <br />
              • onSubmit - Receives the collected values object
              <br />
              • columns - 1 | 2 | 3 | 4 grid layout (fields use `span`)
              <br />
              • initialValues - Prefill values by field name
              <br />
              • showReset - Show a reset button
              <br />
              • onCancel - Optional cancel handler / button
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* MultiStepForm */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>MultiStepForm</Title>
            <Text size="sm" c="dimmed">
              Wizard-style flow that walks users through ordered steps with a
              stepper, per-step validation and back / next navigation.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <MultiStepForm
              steps={onboardingSteps}
              completeLabel="Finish"
              onComplete={() => console.log('Onboarding complete')}
              onCancel={() => console.log('Cancelled')}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const steps = [
  {
    title: 'Account',
    description: 'Login details',
    content: <AccountFields />,
    validate: () => form.isValid('email'),
  },
  {
    title: 'Profile',
    description: 'Personal info',
    content: <ProfileFields />,
  },
  {
    title: 'Review',
    description: 'Confirm',
    content: <ReviewSummary />,
  },
];

<MultiStepForm
  steps={steps}
  completeLabel="Finish"
  onComplete={() => {
    // Submit the wizard
  }}
  onCancel={() => {
    // Abort the wizard
  }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Step Interface:
            </Text>
            <Code block language="typescript">
{`interface FormStep {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  validate?: () => boolean | Promise<boolean>;
  onEnter?: () => void;
  onLeave?: () => void;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* SearchableSelect */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>SearchableSelect</Title>
            <Text size="sm" c="dimmed">
              A single-select combobox with client-side filtering, option
              groups, descriptions, clear and async load-more support.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example:
            </Text>
            <SearchableSelect
              label="Assignee"
              placeholder="Search people..."
              options={assigneeOptions}
              value={assignee}
              onChange={setAssignee}
              clearable
            />
            <Text size="xs" c="dimmed">
              Selected value: {assignee ?? 'none'}
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const options = [
  { value: 'u1', label: 'Oscar Martinez', description: 'Finance', group: 'Finance' },
  { value: 'u3', label: 'Jim Halpert', description: 'Sales Lead', group: 'Sales' },
  { value: 'u4', label: 'Pam Beesly', description: 'Office Admin', group: 'Operations' },
];

const [value, setValue] = useState<string | null>(null);

<SearchableSelect
  label="Assignee"
  placeholder="Search people..."
  options={options}
  value={value}
  onChange={setValue}
  clearable
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Option Interface:
            </Text>
            <Code block language="typescript">
{`interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  group?: string;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Config-driven Forms
              </Text>
              <Text size="xs" c="dimmed">
                Use DynamicForm to render settings, contact and request forms
                from a JSON schema without hand-writing each input.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Guided Wizards
              </Text>
              <Text size="xs" c="dimmed">
                Use MultiStepForm for onboarding, checkout and setup flows that
                break a long form into manageable validated steps.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Large Pick Lists
              </Text>
              <Text size="xs" c="dimmed">
                Use SearchableSelect for assignees, accounts or products where
                users need to search and optionally load more results.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
